<template>
  <div class="item-master-upload-container">
    <b-card class="upload-card">

      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="text-center">
          <b-spinner variant="primary" label="Loading"></b-spinner>
          <p class="mt-2 mb-0">{{ loadingMessage }}</p>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <b-alert
        v-if="successMessage"
        variant="success"
        dismissible
        @dismissed="successMessage = ''"
        class="mb-3"
      >
        <i class="fa fa-check-circle mr-2"></i>
        {{ successMessage }}
      </b-alert>

      <b-alert
        v-if="errorMessage"
        variant="danger"
        dismissible
        @dismissed="errorMessage = ''"
        class="mb-3"
      >
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMessage }}
      </b-alert>

      <!-- File Upload Section -->
      <b-row>
        <b-col md="12" lg="6">
          <div class="file-upload-section">
            <label class="upload-label">
              <i class="fa fa-file-excel-o mr-2" style="color: #28a745"></i>
              Select Excel File
            </label>
            <b-form-file
              v-model="selectedFile"
              :state="fileState"
              accept=".xlsx,.xls"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              @input="handleFile"
              class="mb-2"
              :disabled="isLoading"
            ></b-form-file>
            <small class="form-text text-muted">
              <i class="fa fa-info-circle mr-1"></i>
              Accepted formats: .xlsx, .xls (Excel files only)
            </small>
            <div class="mt-2">
                 <a href="https://uploadsp.pridel.com/Pridel_Reports/ItemMasterSample.xlsx" target="_blank">Download Sample File</a>
            </div>
          </div>
        </b-col>
        <b-col md="12" lg="6">
          <div v-if="selectedFile" class="file-info-section">
            <b-card bg-variant="light" class="mb-0">
              <div class="d-flex align-items-center">
                <i
                  class="fa fa-file-excel-o mr-2"
                  style="font-size: 24px; color: #28a745"
                ></i>
                <div class="flex-grow-1">
                  <strong>{{ selectedFile.name }}</strong>
                  <br />
                  <small class="text-muted">{{
                    formatFileSize(selectedFile.size)
                  }}</small>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="clearFile"
                  :disabled="isLoading"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </b-card>
          </div>
        </b-col>
      </b-row>

      <!-- Data Preview Section -->
      <div v-if="jsonData.length > 0" class="preview-section mt-4">
        <b-card>
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <i class="fa fa-table mr-2"></i>
                <strong>Data Preview</strong>
                <b-badge variant="info" class="ml-2"
                  >{{ searchedPreviewData.length }} Records</b-badge
                >
              </div>
              <div class="d-flex align-items-center">
                 <b-form-input
                  v-model="searchQuery"
                  placeholder="Search Item Name..."
                  size="sm"
                  class="mr-2"
                  style="max-width: 200px;"
                ></b-form-input>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="togglePreview"
                >
                  <i
                    class="fa"
                    :class="showPreview ? 'fa-chevron-up' : 'fa-chevron-down'"
                  ></i>
                  {{ showPreview ? "Hide" : "Show" }} Preview
                </b-button>
              </div>
            </div>
          </template>

          <div v-show="showPreview">
            <div
              v-if="searchedPreviewData.length > 0"
              class="table-responsive"
              style="max-height: 400px; overflow: auto"
            >
              <table
                class="table table-striped table-bordered table-hover table-sm"
              >
                <thead
                  class="thead-light"
                  style="position: sticky; top: 0; z-index: 10"
                >
                  <tr>
                    <th>SN</th>
                    <th
                      v-for="column in requiredColumns"
                      :key="column"
                      style="min-width: 120px"
                    >
                      {{ column }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in paginatedPreviewData" :key="index">
                    <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                    <td v-for="column in requiredColumns" :key="column">
                      {{ row[column] || "-" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="alert alert-warning mb-0">
              <i class="fa fa-exclamation-triangle mr-2"></i>
              No valid data found. Please ensure your Excel file contains required columns.
            </div>
            <!-- Per Page Selector -->
            <div
              class="d-flex justify-content-between align-items-center mt-3 mb-2"
            >
              <div class="d-flex align-items-center">
                <label class="mr-2 mb-0">Rows per page:</label>
                <b-form-select
                  v-model="perPage"
                  :options="pageOptions"
                  size="sm"
                  @change="currentPage = 1"
                  class="w-auto"
                ></b-form-select>
              </div>
              <div>
                <small class="text-muted">
                  Showing {{ (currentPage - 1) * perPage + 1 }} to
                  {{ Math.min(currentPage * perPage, totalPreviewRows) }} of
                  {{ totalPreviewRows }} records
                </small>
              </div>
            </div>

            <!-- Pagination -->
            <b-pagination
              v-if="totalPreviewRows > perPage"
              v-model="currentPage"
              :total-rows="totalPreviewRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="mt-3"
            ></b-pagination>
          </div>
        </b-card>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-4">
        <b-row>
          <b-col md="12" class="text-right">
            <b-button
              variant="outline-secondary"
              @click="clearAll"
              :disabled="isLoading || (!selectedFile && jsonData.length === 0)"
              class="mr-2"
            >
              <i class="fa fa-times mr-1"></i>
              Clear All
            </b-button>
            <b-button
              variant="primary"
              @click="submit"
              :disabled="jsonData.length === 0 || isLoading"
              class="px-4"
            >
              <i class="fa fa-upload mr-1"></i>
              {{ isLoading ? "Uploading..." : "Submit Data" }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import ItemMasterService from "../../../services/ItemMasterService.js";
import CustomLoader from "../../common/customLoader.vue";

export default {
  name: "ItemMasterBulkUpload",
  components: {
    CustomLoader,
  },
  data() {
    return {
      selectedFile: null,
      jsonData: [],
      isLoading: false,
      loadingMessage: "Processing file...",
      successMessage: "",
      errorMessage: "",
      showPreview: true,
      fileState: null,
      // Pagination properties
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 15, 25, 50],
      searchQuery: "", // Search query
    };
  },
  computed: {
    // Filtered preview data with only required columns
    filteredPreviewData() {
      if (!this.jsonData || this.jsonData.length === 0) {
        return [];
      }

      return this.jsonData
        .map((row) => {
          console.log("Processing Row:", row); // Debug log
          const filteredRow = {};
            
          // Mapping logic
          const keyMap = {
            "item_name": ["Item Name"],
            "item_code": ["Item Code"],
            "item_category": ["Item Category"],
            "part_number": ["Part Number"],
            "spare_part_location": ["Spare Part Location"],
            "location": ["Location"],
            "workshop_location": ["Workshop Location"],
            "rate_per_unit": ["Rate Per Unit"],
            "quantity": ["Quantity Available"],
            "unit": ["Unit"],
            "maintenance_type": ["Maintenance Type"],
            "supplier_invoice_number": ["Supplier Invoice Number"],
            "vendor_name": ["Vendor Name"],
            "pridel_po_number": ["Pridel-Po Number"],
            "total_invoice_amount": ["Total Invoice Amount"],
            "stock_value": ["Stock Value"],
            "status": ["Status"],
            "remarks": ["Remarks"]
          };
            
          for (const [targetKey, sourceKeys] of Object.entries(keyMap)) {
               const foundKey = this.findKey(row, sourceKeys);
               if (foundKey) {
                   filteredRow[targetKey] = row[foundKey];
               }
          }

          return filteredRow;
        })
        .filter((row) => {
          // Filter out empty rows (rows with no data)
          if (Object.keys(row).length === 0) {
            return false;
          }
          // Validate required fields
          if (!row["item_name"]) return false;
          
          return true;
        });
    },
    // Filtered data based on search query
    searchedPreviewData() {
      if (!this.searchQuery) {
        return this.filteredPreviewData;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return this.filteredPreviewData.filter(row => 
        row["item_name"] && String(row["item_name"]).toLowerCase().includes(query)
      );
    },
    // Required column names in order
    requiredColumns() {
      return [
        "item_name",
        "item_code",
        "item_category",
        "part_number",
        "spare_part_location",
        "location",
        "workshop_location",
        "rate_per_unit",
        "quantity",
        "unit",
        "maintenance_type",
        "supplier_invoice_number",
        "vendor_name",
        "pridel_po_number",
        "total_invoice_amount",
        "stock_value",
        "status",
        "remarks"
      ];
    },
    // Pagination computed properties
    paginatedPreviewData() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.searchedPreviewData.slice(start, end);
    },
    totalPreviewRows() {
      return this.searchedPreviewData.length;
    },
  },
  methods: {
    handleFile(file) {
      let actualFile = null;

      if (file instanceof File) {
        actualFile = file;
      } else if (file instanceof FileList && file.length > 0) {
        actualFile = file[0];
      } else if (file && typeof file === "object" && file.name) {
        actualFile = file;
      } else if (!file || file === null) {
        this.fileState = false;
        this.jsonData = [];
        this.selectedFile = null;
        return;
      } else {
        actualFile = this.selectedFile;
      }

      if (!actualFile) {
        this.fileState = false;
        this.jsonData = [];
        this.selectedFile = null;
        return;
      }

      // Validate file type
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];

      const fileExtension =
        "." + actualFile.name.split(".").pop().toLowerCase();

      if (
        !validTypes.includes(actualFile.type) &&
        !validExtensions.includes(fileExtension)
      ) {
        this.errorMessage = "Please upload a valid Excel file (.xlsx or .xls)";
        this.fileState = false;
        this.selectedFile = null;
        return;
      }

      // Validate file size (10 MB limit)
      const maxSize = 10 * 1024 * 1024; // 10 MB
      if (actualFile.size > maxSize) {
        this.errorMessage =
          "File size exceeds 10 MB limit. Please upload a smaller file.";
        this.fileState = false;
        this.selectedFile = null;
        return;
      }

      this.fileState = true;
      this.errorMessage = "";
      this.successMessage = "";
      this.isLoading = true;
      this.loadingMessage = "Reading Excel file...";

      const reader = new FileReader();

      reader.onload = (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: "binary" });

          if (workbook.SheetNames.length === 0) {
            throw new Error("Excel file is empty or invalid");
          }

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          this.jsonData = XLSX.utils.sheet_to_json(sheet, {
            defval: null,
            raw: false,
          });

          console.log("Loaded Excel Data:", this.jsonData); // Debug log

          if (this.jsonData.length === 0) {
            this.errorMessage =
              "No data found in the Excel file. Please check your file.";
            this.fileState = false;
            this.jsonData = [];
          } else {
            this.successMessage = `Successfully loaded ${this.jsonData.length} records from Excel file.`;
            this.showPreview = true;
            this.currentPage = 1; // Reset pagination on new data
          }
        } catch (error) {
          console.error("Error reading file:", error);
          this.errorMessage =
            error.message ||
            "Error reading Excel file. Please ensure it's a valid Excel file.";
          this.fileState = false;
          this.jsonData = [];
          this.selectedFile = null;
        } finally {
          this.isLoading = false;
        }
      };

      reader.onerror = () => {
        this.errorMessage = "Error reading file. Please try again.";
        this.fileState = false;
        this.isLoading = false;
        this.jsonData = [];
        this.selectedFile = null;
      };

      reader.readAsBinaryString(actualFile);
    },

    async submit() {
      if (this.jsonData.length === 0) {
        this.errorMessage =
          "No data to submit. Please upload an Excel file first.";
        return;
      }

      this.isLoading = true;
      this.loadingMessage = "Uploading data...";
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const filteredData = this.filteredPreviewData; // Use the computed property which already maps keys

        if (filteredData.length === 0) {
          this.errorMessage =
            "No valid data found.";
          this.isLoading = false;
          return;
        }
        
        // Transform keys to match what backend expects (snake_case)
        const payloadData = filteredData.map((row) => {
          const newRow = Object.assign({}, row);
          newRow["quantity_available"] = row["quantity"]; // Map quantity to quantity_available for backend
          return newRow;
        });

        const payload = {
          data: payloadData,
        };

        const response = await ItemMasterService.bulkUpload(payload);

        if (response.data && response.data.status) {
          this.successMessage =
            response.data.message ||
            `Successfully uploaded ${filteredData.length} records.`;

          // Emit success event to parent component
          this.$emit("upload-success", {
            count: filteredData.length,
            message: response.data.message,
          });

          // Clear data after successful upload
          setTimeout(() => {
            this.clearAll();
          }, 2000);
        } else {
          this.errorMessage =
            response.data.message || "Failed to upload data. Please try again.";
        }
      } catch (error) {
        console.error("Error submitting data:", error);
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          "An error occurred while uploading data. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    clearFile() {
      this.selectedFile = null;
      this.jsonData = [];
      this.fileState = null;
      this.errorMessage = "";
      this.successMessage = "";
    },

    clearAll() {
      this.clearFile();
      this.showPreview = false;
      this.currentPage = 1;
    },

    togglePreview() {
      this.showPreview = !this.showPreview;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    findKey(obj, possibleKeys) {
      if (!obj) return null;

      for (const key of possibleKeys) {
        // Direct match
        if (obj.hasOwnProperty(key)) {
          return key;
        }

        // Case-insensitive and trimmed match
        const lowerKey = key.toLowerCase().trim();
        for (const objKey in obj) {
          if (objKey.toLowerCase().trim() === lowerKey) {
            return objKey;
          }
        }
      }
      return null;
    },
  },
};
</script>

<style scoped>
.item-master-upload-container {
  padding: 20px;
  background-color: #f4f6f9;
}

.upload-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
}

.upload-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  display: block;
}

.file-upload-section {
  margin-bottom: 1rem;
}

.file-info-section {
  margin-bottom: 1rem;
}

.preview-section {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-responsive {
  border-radius: 4px;
  overflow: auto;
}

.table {
  margin-bottom: 0;
  font-size: 13px;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
  cursor: default;
}

.table td {
  vertical-align: middle;
  padding: 8px 12px;
}

.action-buttons {
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
}

@media (max-width: 768px) {
  .item-master-upload-container {
    padding: 15px;
  }

  .action-buttons .text-right {
    text-align: center !important;
  }
}
</style>



// WEBPACK FOOTER //
// ItemMasterBulkUpload.vue?d5e506b6